import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Library, Loader2, Music2, Pause, Play, Sparkles, Trash2, Upload, VolumeX, Wand2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { absUrl, autoScorePreviewUrl, composeAiScore, errMsg, musicFromAsset, removeMusic, uploadMusic } from '../../lib/aiStudioApi';
import { AssetPicker } from './assets/AssetPicker';
import { cn } from '../../lib/utils';
import { useSmoothPlayer } from './useSmoothPlayer';

const AI_SCORE_CREDITS = 24;
const MOODS = ['cinematic', 'warm', 'uplifting', 'romantic', 'epic', 'comedic', 'whimsical', 'suspenseful', 'gritty', 'dark', 'melancholic', 'noir'];
const OPTIONS = [
  ['auto', 'Auto score', Wand2, 'Free'],
  ['track', 'AI score or your track', Sparkles, 'Optional'],
  ['off', 'No music', VolumeX, ''],
];

const PlayBtn = ({ url, player, testId, label }) => {
  const on = player.playing === url;
  return (
    <button type="button" onClick={() => player.play(url).catch((e) => { console.warn('[music preview]', e); toast.error(`Preview could not play — ${e?.name === 'NotSupportedError' ? 'this browser cannot play this audio' : 'try again'}`); })} data-testid={testId}
      aria-label={on ? `Stop ${label}` : `Preview ${label}`}
      className={cn('grid h-6 w-6 shrink-0 place-items-center rounded-full transition-colors duration-200',
        on ? 'bg-amber-400 text-neutral-900' : 'bg-neutral-800 text-neutral-300 hover:bg-neutral-700')}>
      {player.loading === url ? <Loader2 size={11} className="animate-spin" /> : on ? <Pause size={11} /> : <Play size={11} className="ml-px" />}
    </button>
  );
};

function MoodPicker({ mood, tone, player, onPick }) {
  return (
    <div className="space-y-2" data-testid="music-mood-picker">
      <div className="text-xs text-neutral-400">Mood <span className="text-neutral-600">· defaults to the movie tone ({tone}) · tap ▶ for a free preview</span></div>
      <div className="flex flex-wrap gap-1.5">
        {MOODS.map((m) => (
          <div key={m} className={cn('flex items-center gap-1.5 rounded-full border py-0.5 pl-0.5 pr-2.5 text-[11px] transition-colors duration-200',
            mood === m ? 'border-amber-400 bg-amber-500/10 text-neutral-100' : 'border-neutral-800 text-neutral-400 hover:border-neutral-600')}>
            <PlayBtn url={autoScorePreviewUrl(m)} player={player} testId={`music-mood-preview-${m}`} label={m} />
            <button type="button" onClick={() => onPick(m)} className="capitalize" data-testid={`music-mood-${m}`}>
              {m}{m === tone && <span className="ml-1 text-neutral-600">(tone)</span>}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function VolumeControl({ value, onChange, onCommit }) {
  return (
    <div className="flex items-center gap-3" data-testid="music-volume">
      <span className="w-40 shrink-0 text-xs text-neutral-400">Music level under dialogue</span>
      <Slider value={[value]} min={0} max={100} step={1} onValueChange={([v]) => onChange(v)} onValueCommit={([v]) => onCommit(v)}
        className="max-w-xs" data-testid="music-volume-slider" />
      <span className="w-10 text-right font-mono text-xs text-neutral-200" data-testid="music-volume-value">{value}%</span>
    </div>
  );
}

function TrackPanel({ project, busy, run, player }) {
  const ref = useRef(null);
  const [picking, setPicking] = useState(false);
  const m = project.music;
  const ai = project.music_ai || {};
  const composing = ai.status === 'running';
  const compose = () => {
    if (!window.confirm(`Compose an AI score for this production? About ${AI_SCORE_CREDITS} credits, once.`)) return;
    run(() => composeAiScore(project.id), 'Composing your AI score — about 1 to 3 minutes');
  };
  return (
    <div className="space-y-3">
      {m && (
        <div className="flex flex-wrap items-center gap-3" data-testid="music-track">
          <PlayBtn url={absUrl(m.url)} player={player} testId="music-track-preview" label={m.name} />
          <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] text-amber-200">{m.source === 'ai' ? 'AI score' : m.source === 'library' ? 'Asset Library' : 'Uploaded'}</span>
          <span className="max-w-[260px] truncate text-xs text-neutral-300" data-testid="music-name">{m.name}</span>
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => { player.stop(); run(() => removeMusic(project.id), 'Back to the free auto score'); }}
            className="text-neutral-400 hover:text-red-300" data-testid="music-remove-btn"><Trash2 size={13} /></Button>
        </div>
      )}
      <input ref={ref} type="file" accept=".mp3,.wav,.m4a,.aac,.ogg,audio/*" className="hidden" data-testid="music-file-input"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) run(() => uploadMusic(project.id, f), 'Music added'); }} />
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" disabled={busy || composing} onClick={compose} className="bg-amber-500 text-neutral-900 hover:bg-amber-400" data-testid="music-ai-compose-btn">
          {composing ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Sparkles size={13} className="mr-1" />}
          {composing ? 'Composing AI score…' : `${m?.source === 'ai' ? 'Recompose' : 'Compose'} AI score · ${AI_SCORE_CREDITS} cr`}
        </Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => ref.current?.click()} className="border-neutral-700 text-neutral-200" data-testid="music-upload-btn">
          <Upload size={13} className="mr-1" /> Upload your own (optional)
        </Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => setPicking(true)} className="border-neutral-700 text-neutral-200" data-testid="music-library-btn">
          <Library size={13} className="mr-1" /> From Asset Library
        </Button>
      </div>
      <AssetPicker open={picking} onOpenChange={setPicking} folders={['music']} title="Pick a music track — reuse it in any production"
        onPick={(a) => run(() => musicFromAsset(project.id, a.id), `"${a.name}" is now the music bed`)} />
      {ai.status === 'failed' && <div className="text-[11px] text-red-300" data-testid="music-ai-error">AI score failed: {ai.error || 'please retry'} — the free auto score is used meanwhile.</div>}
    </div>
  );
}

/** Music bed — automatic by default: a free score in the chosen mood, blended across the whole movie and ducked under dialogue. */
export function MusicBed({ project, onPatch, onChanged }) {
  const [busy, setBusy] = useState(false);
  const saved = project.music_mode || (project.music ? 'track' : 'auto');
  const [view, setView] = useState(saved);
  const [volume, setVolume] = useState(project.music_volume ?? 55);
  const player = useSmoothPlayer(volume / 100);
  useEffect(() => { setView(saved); }, [saved]);
  useEffect(() => { setVolume(project.music_volume ?? 55); }, [project.music_volume]);
  const composing = (project.music_ai || {}).status === 'running';
  useEffect(() => {
    if (!composing) return undefined;
    const t = setInterval(onChanged, 6000);
    return () => clearInterval(t);
  }, [composing, onChanged]);
  const run = async (fn, ok) => { setBusy(true); try { await fn(); toast.success(ok); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); onChanged(); } };
  const pick = (v) => { setView(v); if (v === 'off') player.stop(); if (v !== 'track' || project.music) onPatch({ music_mode: v }); };
  const tone = project.tone || 'cinematic';
  const mood = project.music_mood || tone;
  return (
    <div className="space-y-3 rounded-xl border border-neutral-800 bg-neutral-900/40 p-4" data-testid="music-bed">
      <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-neutral-100">
        <Music2 size={15} className="text-amber-300" /> Music bed
        <span className="text-[11px] font-normal text-neutral-500">· automatic · blended across the whole movie · glides down under every line and back up</span>
      </div>
      <div className="grid gap-2 sm:grid-cols-3" data-testid="music-mode-switch">
        {OPTIONS.map(([v, title, Icon, tag]) => (
          <button key={v} type="button" onClick={() => pick(v)} data-testid={`music-mode-${v}`}
            className={cn('flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-xs transition-colors duration-200',
              view === v ? 'border-amber-400 bg-amber-500/10 text-neutral-100' : 'border-neutral-800 text-neutral-400 hover:border-neutral-600')}>
            <Icon size={14} className="text-amber-300" /> <span className="font-medium">{title}</span>
            {tag && <span className="ml-auto text-[10px] text-neutral-500">{tag}</span>}
          </button>
        ))}
      </div>
      {view === 'auto' && (
        <div className="space-y-2" data-testid="music-auto-panel">
          <MoodPicker mood={mood} tone={tone} player={player} onPick={(m) => onPatch({ music_mood: m === tone ? '' : m })} />
          <div className="text-[11px] text-neutral-500">Composed in the <strong className="text-neutral-300">{mood}</strong> mood at the exact length of your movie — fades in and out, no loop seams. 0 credits.</div>
        </div>
      )}
      {view === 'track' && <TrackPanel project={project} busy={busy} run={run} player={player} />}
      {view !== 'off' && <VolumeControl value={volume} onChange={setVolume} onCommit={(v) => onPatch({ music_volume: v })} />}
      {view === 'off' && <div className="text-xs text-neutral-500" data-testid="music-off-note">No music bed — the Final Cut keeps dialogue and in-clip sound only.</div>}
    </div>
  );
}
